import * as pdfMake from 'pdfmake/build/pdfmake.min';

import { Procedimento } from '@/models/Procedimento';

interface PatientJsonType {
  nome: string;
  data: string;
  idade: string;
  genero: string;
}
interface PractitionerJsonType {
  nome: string;
  identificador: string | undefined;
  especialidade: string;
  genero: string;
}

const printEncaminhamento = (
  encaminhamento: Procedimento,
  logoUrl: string,
  entidadeDeSaude: string,
  paciente: PatientJsonType,
  profissional: PractitionerJsonType,
  dadosAtendimento: { motivo: string; observacoes: string[] },
): void => {
  const indicacaoContent = {
    text: [
      { text: 'Encaminho o paciente ' },
      { text: `${paciente.nome}`, bold: true },
      dadosAtendimento && {
        text: ` que apresenta os seguintes dados: (${
          dadosAtendimento.motivo
            ? `Motivo do atendimento:${dadosAtendimento.motivo}; `
            : ''
        }${
          dadosAtendimento.observacoes.length
            ? `Sinais Vitais: ${dadosAtendimento.observacoes.join(', ')}`
            : ''
        }) para avaliação.`,
      },
    ],
    fontSize: 9,
    color: '#2F3139',
  };

  const tableLayout = {
    paddingBottom() {
      return 5;
    },
    paddingTop() {
      return 5;
    },
    fillColor(rowIndex: number) {
      return rowIndex === 0 ? '#D9DBE3' : '#fff';
    },
    hLineWidth() {
      return 1;
    },
    vLineWidth() {
      return 1;
    },
    paddingLeft(i: number) {
      return i === 0 ? 11 : 8;
    },
    hLineColor() {
      return '#D9DBE3';
    },
    vLineColor() {
      return '#D9DBE3';
    },
  };

  const headerLayout = {
    paddingBottom() {
      return 5;
    },
    paddingTop() {
      return 5;
    },
    fillColor() {
      return '#F0F2F5';
    },
    hLineWidth() {
      return 1;
    },
    vLineWidth() {
      return 1;
    },
    paddingLeft(i: number) {
      return i === 0 ? 11 : 8;
    },
    hLineColor() {
      return '#D9DBE3';
    },
    vLineColor() {
      return '#D9DBE3';
    },
  };

  const headerWithLogo = {
    style: 'header',
    table: {
      widths: [120, '*'],
      body: [
        [
          {
            alignment: 'center',
            columns: [
              {
                image: 'logo',
                fit: [100, 30],
                margin: [0, 14],
              },
            ],
          },
          [
            {
              text: 'ENCAMINHAMENTO MÉDICO',
              fontSize: 12,
              color: '#2F3139',
              bold: true,
              margin: [0, 5, 0, 5],
            },
            {
              margin: [0, 0, 0, 3],
              columns: [
                {
                  text: 'Entidade de saúde: ',
                  fontSize: 9,
                  color: '#2F3139',
                  bold: true,
                  width: 'auto',
                },
                {
                  text: entidadeDeSaude || '',
                  fontSize: 9,
                  color: '#2F3139',
                  margin: [3, 0, 0, 0],
                },
              ],
              fillColor: '#F0F2F5',
            },
            {
              columns: [
                {
                  text: 'Data de emissão: ',
                  fontSize: 9,
                  color: '#2F3139',
                  bold: true,
                  width: 'auto',
                  fillColor: '#F0F2F5',
                },
                {
                  text: new Date().toLocaleDateString(),
                  fontSize: 9,
                  color: '#2F3139',
                  margin: [3, 0, 0, 0],
                  fillColor: '#F0F2F5',
                },
              ],
            },
          ],
        ],
      ],
    },
    layout: headerLayout,
  };

  const headerWithoutLogo = {
    style: 'header',
    table: {
      widths: ['*'],
      body: [
        [
          [
            {
              text: 'ENCAMINHAMENTO MÉDICO',
              fontSize: 12,
              color: '#2F3139',
              bold: true,
              margin: [0, 0, 0, 5],
              fillColor: '#F0F2F5',
            },
            {
              margin: [0, 0, 0, 3],
              columns: [
                {
                  text: 'Entidade de saúde: ',
                  fontSize: 9,
                  color: '#2F3139',
                  bold: true,
                  width: 'auto',
                  fillColor: '#F0F2F5',
                },
                {
                  text: entidadeDeSaude || '',
                  fontSize: 9,
                  color: '#2F3139',
                  margin: [3, 0, 0, 0],
                  fillColor: '#F0F2F5',
                },
              ],
            },
            {
              columns: [
                {
                  text: 'Data de emissão: ',
                  fontSize: 9,
                  color: '#2F3139',
                  bold: true,
                  width: 'auto',
                },
                {
                  text: new Date().toLocaleDateString(),
                  fontSize: 9,
                  color: '#2F3139',
                  margin: [3, 0, 0, 0],
                },
              ],
            },
          ],
        ],
      ],
    },
    layout: headerLayout,
  };

  let header = {};
  if (!logoUrl || logoUrl.includes('.svg')) {
    header = headerWithoutLogo;
  } else {
    header = headerWithLogo;
  }

  const docDefinition = {
    pageSize: 'A4',
    content: [
      header,
      {
        style: 'table',
        table: {
          widths: ['*'],
          heights: ['auto', 40],
          body: [
            [
              {
                text: 'Dados do paciente',
                color: '#2F3139',
                bold: true,
                fontSize: 10,
                lineHeight: 1,
                colspan: 4,
                fillColor: '#F0F2F5',
              },
            ],
            [
              {
                margin: [0, 0, 0, 3],
                columns: [
                  {
                    text: [
                      { text: `Nome:\n`, bold: true },
                      {
                        text: `${paciente.nome}`,
                      },
                    ],
                    fontSize: 9,
                    color: '#2F3139',
                    width: '25%',
                    fillColor: '#F0F2F5',
                  },
                  {
                    text: [
                      { text: `Sexo:\n`, bold: true },
                      {
                        text: `${paciente.genero}`,
                      },
                    ],
                    fontSize: 9,
                    color: '#2F3139',
                    width: '25%',
                    fillColor: '#F0F2F5',
                  },
                  {
                    text: [
                      { text: `Data de nascimento:\n`, bold: true },
                      {
                        text: `${paciente.data}`,
                      },
                    ],
                    fontSize: 9,
                    color: '#2F3139',
                    width: '25%',
                    fillColor: '#F0F2F5',
                  },
                  {
                    text: [
                      { text: `Idade:\n`, bold: true },
                      {
                        text: `${paciente.idade}`,
                      },
                    ],
                    fontSize: 9,
                    color: '#2F3139',
                    width: '25%',
                    fillColor: '#F0F2F5',
                  },
                ],
              },
            ],
          ],
        },
        layout: tableLayout,
      },
      {
        style: 'table',
        table: {
          widths: ['*'],
          heights: ['auto', 40],
          body: [
            [
              {
                text: 'Encaminhar para',
                color: '#2F3139',
                bold: true,
                fontSize: 10,
                lineHeight: 1,
                fillColor: '#F0F2F5',
              },
            ],
            [
              {
                margin: [0, 10, 10, 0],
                text: encaminhamento.nome,
                fontSize: 9,
                color: '#2F3139',
                width: 'auto',
                alignment: 'center',
              },
            ],
          ],
        },
        layout: tableLayout,
      },
      {
        style: 'table',
        table: {
          widths: ['*'],
          heights: ['auto', 200],
          body: [
            [
              {
                text: 'Indicação',
                color: '#2F3139',
                bold: true,
                fontSize: 10,
                lineHeight: 1,
                fillColor: '#F0F2F5',
              },
            ],
            [indicacaoContent],
          ],
        },
        layout: tableLayout,
      },
      {
        margin: [100, 160, 100, 10],
        alignment: 'center',
        table: {
          widths: [300],
          headerRows: 1,
          body: [[{ text: '' }], [{ text: `Dr. ${profissional.nome}` }]],
        },
        layout: {
          hLineWidth(i: number) {
            return i === 0 ? 1 : 0;
          },
          vLineWidth() {
            return 0;
          },
          hLineColor() {
            return '#2F3139';
          },
        },
      },
    ],
    defaultStyle: {
      font: 'SansPro',
    },
    footer(currentPage: number, pageCount: number) {
      return {
        columns: [
          {
            image:
              'data:image/png;base64,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',
            alignment: 'right',
            fit: [80, 80],
            width: '57%',
          },
          {
            text: `Página ${currentPage.toString()} de ${pageCount}`,
            alignment: 'right',
            width: '43%',
            margin: [0, 0, 40, 0],
          },
        ],
      };
    },
    styles: {
      header: {
        margin: [0, 0, 0, 12],
      },
      table: {
        margin: [0, 0, 0, 12],
      },
      footer: {
        margin: [0, 0, 0, 60],
      },
    },
    images: {},
  };

  if (logoUrl) {
    docDefinition.images = {
      logo: logoUrl,
    };
  }
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const pdfDocGenerator = pdfMake.createPdf(docDefinition, null, {
    SansPro: {
      normal:
        'https://cdn.jsdelivr.net/npm/source-sans-pro@3.6.0/TTF/SourceSansPro-Regular.ttf',
      bold:
        'https://cdn.jsdelivr.net/npm/source-sans-pro@3.6.0/TTF/SourceSansPro-Bold.ttf',
    },
  });

  pdfDocGenerator.getBlob(blob => {
    const blobURL = URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');
    document.body.appendChild(iframe);

    iframe.style.display = 'none';
    iframe.src = blobURL;
    iframe.onload = () => {
      setTimeout(() => {
        iframe.focus();
        iframe.contentWindow?.print();
      });
    };
  });
};

export { printEncaminhamento };
